import {
  ChatItemV2,
  ChatItemV2Assistant,
  ChatItemV2ToolProgress,
  FrontendCopilotInteraction,
} from '@/lib/type'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

export type CopilotStatus = 'idle' | 'sending' | 'streaming' | 'error'

interface StreamingChatState {
  interactions: FrontendCopilotInteraction[]
  status: CopilotStatus
  currentStreamingMessageId: string | null
  errorMessage: string | null
  typewriterQueue: ChatItemV2Assistant[]
  latestResults?: any // Store the latest workflow execution results
  workflowExecution: {
    isExecuting: boolean
    toolId?: string
  }
  failedMessage?: {
    content: string
    files?: File[]
    timestamp: string
  } | null
}

export const initialState: StreamingChatState = {
  interactions: [],
  status: 'idle',
  currentStreamingMessageId: null,
  errorMessage: null,
  typewriterQueue: [],
  latestResults: undefined,
  workflowExecution: {
    isExecuting: false,
    toolId: undefined,
  },
  failedMessage: null,
}

// Simple function to flatten frontend interactions to messages (no conversion needed!)
const flattenInteractionsToMessages = (
  interactions: FrontendCopilotInteraction[]
): ChatItemV2[] => {
  return interactions.flatMap((interaction) => interaction.messages)
}

const streamingChatSlice = createSlice({
  name: 'streamingChat',
  initialState,
  reducers: {
    // Interaction management
    setInteractions: (
      state: StreamingChatState,
      action: PayloadAction<FrontendCopilotInteraction[]>
    ) => {
      console.debug('[ChatSlice] setInteractions', action.payload.length, 'interactions')
      state.interactions = action.payload
    },

    addInteraction: (
      state: StreamingChatState,
      action: PayloadAction<FrontendCopilotInteraction>
    ) => {
      console.debug('[ChatSlice] addInteraction', action.payload.id)
      state.interactions.push(action.payload)
    },

    updateInteraction: (
      state: StreamingChatState,
      action: PayloadAction<FrontendCopilotInteraction>
    ) => {
      console.debug('[ChatSlice] updateInteraction', action.payload.id)
      const index = state.interactions.findIndex((i) => i.id === action.payload.id)
      if (index !== -1) {
        state.interactions[index] = action.payload
      }
    },

    removeInteractionsAfter: (state: StreamingChatState, action: PayloadAction<string>) => {
      console.debug('[ChatSlice] removeInteractionsAfter', action.payload)
      const index = state.interactions.findIndex((i) => i.id === action.payload)
      if (index !== -1) {
        state.interactions = state.interactions.slice(0, index + 1)
      }
    },

    clearInteractions: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearInteractions')
      state.interactions = []
      state.typewriterQueue = []
    },

    resetState: (state: StreamingChatState) => {
      console.debug('[ChatSlice] resetState - full state reset')
      // Reset to initial state
      state.interactions = []
      state.status = 'idle'
      state.currentStreamingMessageId = null
      state.errorMessage = null
      state.typewriterQueue = []
      state.latestResults = undefined
      state.workflowExecution = {
        isExecuting: false,
        toolId: undefined,
      }
      state.failedMessage = null
    },

    // Status management
    setStatus: (state: StreamingChatState, action: PayloadAction<CopilotStatus>) => {
      console.debug('[ChatSlice] setStatus', action.payload)
      state.status = action.payload
    },

    // Convenience actions for common status changes
    setIdle: (state: StreamingChatState) => {
      console.debug('[ChatSlice] setIdle')
      state.status = 'idle'
      state.currentStreamingMessageId = null
    },

    setSending: (state: StreamingChatState) => {
      console.debug(
        '[ChatSlice] setSending - Previous status:',
        state.status,
        'Previous errorMessage:',
        state.errorMessage
      )
      state.status = 'sending'
      console.debug('[ChatSlice] setSending - New status:', state.status)
    },

    setStreaming: (state: StreamingChatState, action: PayloadAction<string>) => {
      console.debug('[ChatSlice] setStreaming', action.payload)
      state.status = 'streaming'
      state.currentStreamingMessageId = action.payload
    },

    setError: (state: StreamingChatState, action: PayloadAction<string>) => {
      console.debug('[ChatSlice] setError', action.payload)
      state.status = 'error'
      state.errorMessage = action.payload
      state.currentStreamingMessageId = null
    },

    clearError: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearError')
      state.errorMessage = null
      // Don't change status - let other actions handle that
    },

    // Failed message management
    setFailedMessage: (
      state: StreamingChatState,
      action: PayloadAction<{ content: string; files?: File[] }>
    ) => {
      console.debug('[ChatSlice] setFailedMessage', action.payload)
      state.failedMessage = {
        ...action.payload,
        timestamp: new Date().toISOString(),
      }
    },

    clearFailedMessage: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearFailedMessage')
      state.failedMessage = null
    },

    // Typewriter queue management
    updateTypewriterQueue: (
      state: StreamingChatState,
      action: PayloadAction<ChatItemV2Assistant[]>
    ) => {
      console.debug('[ChatSlice] updateTypewriterQueue', action.payload)
      state.typewriterQueue = action.payload
    },

    removeFromTypewriterQueue: (state: StreamingChatState, action: PayloadAction<string>) => {
      console.debug('[ChatSlice] removeFromTypewriterQueue', action.payload)
      state.typewriterQueue = state.typewriterQueue.filter((item) => item.id !== action.payload)
    },

    clearTypewriterQueue: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearTypewriterQueue')
      state.typewriterQueue = []
    },

    // Convert typewriter message to normal message - works with interactions
    removeTypewriterEffect: (state: StreamingChatState, action: PayloadAction<string>) => {
      console.debug('[ChatSlice] removeTypewriterEffect', action.payload)
      // Find the message in interactions and remove typewriter state
      for (const interaction of state.interactions) {
        const messageIndex = interaction.messages.findIndex((m) => m.id === action.payload)
        if (messageIndex !== -1) {
          const message = interaction.messages[messageIndex]
          if (message.role === 'assistant') {
            const updatedMessage = { ...message }
            delete (updatedMessage as any).typeWriterState
            interaction.messages[messageIndex] = updatedMessage
          }
          break
        }
      }
    },

    // Update typewriter progress for a specific message - works with interactions
    updateTypewriterState: (
      state: StreamingChatState,
      action: PayloadAction<{ messageId: string; characterIndex: number }>
    ) => {
      console.debug('[ChatSlice] updateTypewriterState', action.payload)
      // Find the message in interactions and update typewriter state
      for (const interaction of state.interactions) {
        const messageIndex = interaction.messages.findIndex(
          (m) => m.id === action.payload.messageId
        )
        if (messageIndex !== -1) {
          const message = interaction.messages[messageIndex]
          if (message.role === 'assistant') {
            interaction.messages[messageIndex] = {
              ...message,
              typeWriterState: action.payload.characterIndex,
            } as any
          }
          break
        }
      }
    },

    // Workflow results management
    setLatestResults: (state: StreamingChatState, action: PayloadAction<any>) => {
      console.debug('[ChatSlice] setLatestResults', action.payload)
      state.latestResults = action.payload
    },

    clearLatestResults: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearLatestResults')
      state.latestResults = undefined
    },

    // Workflow execution state management
    setWorkflowExecuting: (
      state: StreamingChatState,
      action: PayloadAction<{ isExecuting: boolean; toolId?: string }>
    ) => {
      console.debug('[ChatSlice] setWorkflowExecuting', action.payload)
      state.workflowExecution = action.payload
    },

    clearWorkflowExecution: (state: StreamingChatState) => {
      console.debug('[ChatSlice] clearWorkflowExecution')
      state.workflowExecution = { isExecuting: false, toolId: undefined }
    },

    // Tool progress message management - works with interactions
    addOrUpdateToolProgress: (
      state: StreamingChatState,
      action: PayloadAction<ChatItemV2ToolProgress>
    ) => {
      // Find the message in the current (last) interaction
      const currentInteraction = state.interactions[state.interactions.length - 1]
      if (!currentInteraction) return

      const messageIndex = currentInteraction.messages.findIndex(
        (m) => m.role === 'tool' && (m as any).tool_call_id === action.payload.tool_call_id
      )

      if (messageIndex !== -1) {
        // Update existing tool message
        currentInteraction.messages[messageIndex] = {
          ...currentInteraction.messages[messageIndex],
          ...action.payload,
        } as any
      } else {
        // Add new tool message to current interaction
        currentInteraction.messages.push({
          id: action.payload.id,
          type: 'message',
          role: 'tool',
          content: (action.payload as any).content || '',
          timestamp: action.payload.timestamp,
          tool_call_id: action.payload.tool_call_id,
          tool: action.payload.tool,
          status: action.payload.status,
          progressMessages: action.payload.progressMessages || [],
          expanded: action.payload.expanded,
          headline: action.payload.headline,
        } as any)
      }
      currentInteraction.updatedAt = action.payload.timestamp
    },
    appendToolProgressMessage: (
      state: StreamingChatState,
      action: PayloadAction<{ tool_call_id: string; progress_message: string }>
    ) => {
      // Find the tool message in current interaction
      const currentInteraction = state.interactions[state.interactions.length - 1]
      if (!currentInteraction) return

      const messageIndex = currentInteraction.messages.findIndex(
        (m) => m.role === 'tool' && (m as any).tool_call_id === action.payload.tool_call_id
      )

      if (messageIndex !== -1) {
        const msg = currentInteraction.messages[messageIndex] as any
        console.log(
          '[appendToolProgressMessage]',
          action.payload.tool_call_id,
          'Adding:',
          action.payload.progress_message,
          'Current:',
          msg.progressMessages
        )
        currentInteraction.messages[messageIndex] = {
          ...msg,
          progressMessages: [...(msg.progressMessages || []), action.payload.progress_message],
        }
        currentInteraction.updatedAt = new Date().toISOString()
      }
    },
    completeToolProgress: (
      state: StreamingChatState,
      action: PayloadAction<{
        tool_call_id: string
        status: 'completed' | 'failed'
        headline?: string
      }>
    ) => {
      // Find the tool message in current interaction
      const currentInteraction = state.interactions[state.interactions.length - 1]
      if (!currentInteraction) return

      const messageIndex = currentInteraction.messages.findIndex(
        (m) => m.role === 'tool' && (m as any).tool_call_id === action.payload.tool_call_id
      )

      if (messageIndex !== -1) {
        const msg = currentInteraction.messages[messageIndex] as any
        currentInteraction.messages[messageIndex] = {
          ...msg,
          status: action.payload.status,
          expanded: false,
          ...(action.payload.headline ? { headline: action.payload.headline } : {}),
        }
        currentInteraction.updatedAt = new Date().toISOString()
      }
    },
  },
})

export const {
  // New interaction-based actions
  setInteractions,
  addInteraction,
  updateInteraction,
  removeInteractionsAfter,
  clearInteractions,
  resetState,
  // Status management
  setStatus,
  setIdle,
  setSending,
  setStreaming,
  setError,
  clearError,
  setFailedMessage,
  clearFailedMessage,
  updateTypewriterQueue,
  removeFromTypewriterQueue,
  clearTypewriterQueue,
  removeTypewriterEffect,
  updateTypewriterState,
  // Workflow results
  setLatestResults,
  clearLatestResults,
  setWorkflowExecuting,
  clearWorkflowExecution,
  // Tool progress
  addOrUpdateToolProgress,
  appendToolProgressMessage,
  completeToolProgress,
} = streamingChatSlice.actions

// Convenience alias
export const clearMessages = clearInteractions

// Export helper functions and types
export { flattenInteractionsToMessages }

// Selectors to derive values from the state enum
export const selectIsSending = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.status === 'sending'

export const selectIsStreaming = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.status === 'streaming'

export const selectCurrentStreamingMessageId = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.currentStreamingMessageId

export const selectErrorMessage = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.errorMessage

export const selectStatus = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.status

export const selectWorkflowExecution = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.workflowExecution

export const selectIsWorkflowExecuting = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.workflowExecution.isExecuting

export const selectFailedMessage = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.failedMessage
// New selectors for interaction-based state
export const selectInteractions = (state: { streamingChat: StreamingChatState }) =>
  state.streamingChat.interactions

// Memoized selector to prevent unnecessary re-renders
export const selectMessages = createSelector([selectInteractions], (interactions) =>
  flattenInteractionsToMessages(interactions)
)

export default streamingChatSlice.reducer
